import {RefObject, useEffect, useRef, useState} from 'react'
import { useLocation } from 'react-router-dom'

type IntersectionObserverResult = {
  isOnScreen: boolean,
  observerKey: string
}

interface IntersectionObserverOptions extends IntersectionObserverInit {
  useOnce?: boolean
}

export const useIntersectionObserver = (
  ref: RefObject<Element>,
  options: IntersectionObserverOptions = {}
): IntersectionObserverResult => {
  const [isIntersecting, setIntersecting] = useState(false)

  const observerKey = useRef('')
  // null so the first observation is accepted even before observerKey is set
  const keyOnLastObservation = useRef<string | null>(null)

  const { useOnce, ...ioOptions } = options

  const location = useLocation()

  useEffect(() => {
    setIntersecting(false)
    const timeout = setTimeout(() => {
      observerKey.current = `${location.pathname}?${location.search}`
    }, 0)

    return () => {
      clearTimeout(timeout)
      setIntersecting(false)
    }
  }, [location])

  useEffect(() => {
    // Reset intersection state when observerKey changes - E.G Route change
    setIntersecting(false)

    if (!ref?.current) return

    // Just set `isIntersecting` true if browser doesn't implement IntersectionObserver. If the use-case
    // is critical and you need to support very old browsers, a polyfill will need to be added.
    if (
      !('IntersectionObserver' in window) ||
      !('IntersectionObserverEntry' in window) ||
      !('intersectionRatio' in window.IntersectionObserverEntry.prototype)
    ) {
      setIntersecting(true)
      // We want to return early, but `useEffect` expects a function as the return value,
      // so we just return a noop function.
      return () => null
    }

    const observer = new IntersectionObserver(([entry]) => {
      const onScreen = entry.isIntersecting
      const hasNotBeenObserved = keyOnLastObservation.current !== observerKey.current
      if (onScreen && (!useOnce || hasNotBeenObserved)){
        keyOnLastObservation.current = observerKey.current
        setIntersecting(onScreen)
        if (useOnce) {
          observer.disconnect()
        }
      }
    }, ioOptions)

    observer.observe(ref.current)
    // Remove the observer as soon as the component is unmounted
    return () => {
      observer.disconnect()
    }
  }, [ref?.current])

  return {
    isOnScreen: isIntersecting,
    observerKey: observerKey.current
  }
}
